import { useTranslation } from "react-i18next";
import AdminHeader from "../AdminHeader";
import approversService from "../../../services/approvers.service";
import useServiceReducer from "../../../hooks/useServiceReducer";
import approversReducer from "./approversReducer";
import { PagedFilter } from "../../../misc/pagedFilter";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import styles from "./Approvers.module.scss";
import {
  ApproverData,
  AssignationInfo,
  AssignationRemoveResult,
} from "../../../models/approver.model";
import { PaginatedDataTable } from "../../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import { RemoveIcon } from "../../../assets/icons";
import useService from "../../../hooks/useService";
import SpinnerArea from "../../../components/SpinnerArea";
import { PeoplePicker } from "../../../components/SelectPicker/PeoplePickers";
import { useContext, useState } from "react";
import { IUser } from "../../../models/user.model";
import PlaceholderControl from "../../../components/PlaceholderControl";
import MainContext from "../../../context/mainContext";
import { AlertDialog, ConfirmDialog } from "./Approvers.Dialogs";

const Approvers: React.FC = () => {
  const { t } = useTranslation();

  const { service } = useContext(MainContext);

  const [selectedDivDept, setSelectedDivDept] = useState<string | undefined>();
  const [selectedApprover, setSelectedApprover] = useState<IUser | null>(null);

  const [saving, setSaving] = useState<boolean>(false);
  const [showConfirmChange, setShowConfirmChange] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>();

  const [selectedAssignForRemove, setSelectedAssignForRemove] =
    useState<ApproverData>();

  const [remoteValidation, setRemoteValidation] = useState<string>();

  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string>();

  const [activeDivisions, loadingActiveDivisions] = useService(() =>
    approversService.divisionsActive()
  );

  const initFilter: PagedFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortField: "division",
    sortDescending: false,
  };

  const [data, loading, filter, dispatch, , reload] = useServiceReducer(
    approversService.search,
    approversReducer,
    initFilter
  );

  const columns: GridColDef[] = [
    {
      field: "division",
      headerName: t("divDept")!,
      sortable: true,
      width: 120,
    },
    {
      field: "firstName",
      headerName: t("firstName")!,      
      flex: 1,
      sortable: true,
      width: 200,
    },
    {
      field: "lastName",
      headerName: t("lastName")!,
      flex: 1,
      sortable: true,
      width: 200,
    },

    {
      field: "actions",
      headerName: t("actions")!,
      headerClassName: "actions",
      sortable: false,
      cellClassName: "actions",
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(params.row);
            }}
          >
            <RemoveIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleRemove = (assignation: ApproverData) => {
    setSelectedAssignForRemove(assignation);
  };

  const handlePageChange = (pageNumber: number) => {
    dispatch({ type: "PAGE", value: pageNumber });
  };

  const handleSortChange = (field: string, sortDescending: boolean) => {
    dispatch({
      type: "SEARCH",
      value: {
        ...filter,
        sortField: field,
        sortDescending: sortDescending,
      },
    });
  };

  const handleAssign = (fromConfirm?: boolean) => {
    if (selectedDivDept && selectedApprover) {
      service(
        () =>
          fromConfirm
            ? approversService.assignUpdate({
                division: selectedDivDept,
                approver: selectedApprover,
              })
            : approversService.assign({
                division: selectedDivDept,
                approver: selectedApprover,
              }),
        {
          loader: setSaving,
          callback: (data: AssignationInfo) => {
            if (data.assigned) {
              setSaveSuccessMessage(
                t("saveApproverSuccess", {
                  division: selectedDivDept,
                  newApprover: selectedApprover.displayName,
                })!
              );

              setSelectedApprover(null);
              setSelectedDivDept(undefined);
              setShowConfirmChange(false);
              reload();
            } else {
              setConfirmMessage(data.confirmChangeMessage);
              setShowConfirmChange(true);
            }
          },
        }
      );
    }
  };

  const removeAssignation = () => {
    if (selectedAssignForRemove) {
      service(() => approversService.remove(selectedAssignForRemove.id), {
        loader: setSaving,
        callback: (data: AssignationRemoveResult) => {
          if (data.success) {
            reload();
          } else {
            setRemoteValidation(data.message);
          }

          setSelectedAssignForRemove(undefined);
        },
      });
    }
  };

  return (
    <div>
      <AdminHeader text={t("approvers")} />
      <Box className={styles.main}>
        <Paper elevation={3} className={styles.basic + " " + styles.approvers}>
          <div className={styles.assignRow}>
            <FormLabel id="assignApprover">{t("assignApprover")}</FormLabel>
          </div>
          <div className={styles.assignRow}>
            <FormControl fullWidth className={styles.control}>
              <Select
                id="selectedDivisionDepartment"
                name="selectedDivisionDepartment"
                className={`${styles.teamMembers}`}
                fullWidth
                value={selectedDivDept ?? ""}
                onChange={(event) => setSelectedDivDept(event.target.value)}
                displayEmpty
                renderValue={(value) =>
                  value === "" ? (
                    <PlaceholderControl text={t("selectDivisionDepartment")} />
                  ) : (
                    value
                  )
                }
              >
                <MenuItem
                  key={`active_division_empty}`}
                  value={""}
                  className={"selectPlaceholder"}
                >
                  {t("select")}
                </MenuItem>
                {activeDivisions?.map((x) => (
                  <MenuItem key={`active_division_${x}`} value={x!}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className={styles.control}>
              <PeoplePicker
                id="selectedApprover"
                defaultValue={selectedApprover}
                readOnly={false}
                placeholder={t("selectTheApprover").toString()}
                onChange={(newMember) => setSelectedApprover(newMember)}
                onBlur={() => {}}
              />
            </FormControl>
            <Box>
              {saving ? (
                <CircularProgress className={styles.circular} />
              ) : (
                <Button
                  onClick={() => handleAssign()}
                  className={styles.assign}
                  disabled={!selectedApprover || !selectedDivDept || saving}
                >
                  {t("assign")}
                </Button>
              )}
            </Box>
          </div>

          <PaginatedDataTable<ApproverData>
            rows={data ? data.list : []}
            totalRows={data ? data.totalRows : 0}
            initialPage={filter.pageNumber - 1}
            pageSize={filter.pageSize}
            columns={columns}
            handlePageChange={handlePageChange}
            handleSortChange={handleSortChange}
            emptyMessage={t("noRowsMessage", {
              entity: t("approvers"),
            })}
            loading={loading || loadingActiveDivisions}
          />
        </Paper>
      </Box>
      <ConfirmDialog
        open={showConfirmChange}
        onClose={() => setShowConfirmChange(false)}
        onAccept={() => handleAssign(true)}
        text={confirmMessage!}
        saving={saving}
      />

      {selectedAssignForRemove && (
        <ConfirmDialog
          open={true}
          onClose={() => setSelectedAssignForRemove(undefined)}
          onAccept={() => removeAssignation()}
          text={
            t("removeApproverConfirm", {
              currentApprover: `${selectedAssignForRemove.lastName}, ${selectedAssignForRemove.firstName}`,
              division: selectedAssignForRemove.division,
            })!
          }
          saving={saving}
        />
      )}

      {remoteValidation && (
        <AlertDialog
          open={true}
          onClose={() => setRemoteValidation(undefined)}
          text={remoteValidation}
        />
      )}

      {saveSuccessMessage && (
        <AlertDialog
          open={true}
          onClose={() => setSaveSuccessMessage(undefined)}
          text={saveSuccessMessage}
        />
      )}
    </div>
  );
};

export default Approvers;
